import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Users, Briefcase, Mail, UserPlus, TrendingUp, CheckCircle2, FileText, Calendar, Sun } from 'lucide-react';

const ROLE_LABEL = { brigadista: 'Brigadista', gestion: 'Gestor' };
const emptyForm = { name: '', email: '', phone: '', password: '', role: 'brigadista' };

export default function EquipoPage() {
  const { api, user } = useAuth();
  const [team, setTeam] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [t, k] = await Promise.all([
        api.get('/equipo'),
        api.get('/equipo/kpis'),
      ]);
      setTeam(t.data);
      setKpis(k.data);
    } catch (e) {
      toast.error('Error al cargar el equipo');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const brigadistas = team.filter(m => m.role === 'brigadista');
  const gestores = team.filter(m => m.role === 'gestion');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Completa nombre y email');
      return;
    }
    if (!form.phone && !form.password) {
      toast.error('Agrega teléfono WhatsApp o contraseña');
      return;
    }
    setSaving(true);
    try {
      await api.post('/equipo', form);
      toast.success(`${ROLE_LABEL[form.role]} agregado`);
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/equipo/${confirmDelete.id}`);
      toast.success('Miembro eliminado');
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openNew = (role) => { setForm({ ...emptyForm, role }); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="equipo-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Mi Equipo</h1>
          <p className="text-base text-[#52525B] mt-1">
            Distrito {user?.distrito ? `D${user.distrito}` : 'sin asignar'} · {brigadistas.length}/10 brigadistas · {gestores.length}/2 gestores
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-md bg-[#0052FF]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#0052FF]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#52525B] font-semibold">Brigadistas</p>
                <p className="text-sm font-bold text-[#0A0A0A]">{brigadistas.length}/10</p>
              </div>
              <Progress value={brigadistas.length * 10} className="h-2 mt-1.5" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-md bg-[#10B981]/10 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-[#10B981]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#52525B] font-semibold">Gestores</p>
                <p className="text-sm font-bold text-[#0A0A0A]">{gestores.length}/2</p>
              </div>
              <Progress value={gestores.length * 50} className="h-2 mt-1.5" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="equipo" className="w-full">
        <TabsList className="grid w-full sm:w-auto grid-cols-2 rounded-md">
          <TabsTrigger data-testid="tab-equipo" value="equipo">Equipo</TabsTrigger>
          <TabsTrigger data-testid="tab-kpis" value="kpis">KPIs</TabsTrigger>
        </TabsList>

        <TabsContent value="equipo" className="space-y-6 mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <TeamSection
                title="Brigadistas"
                icon={Users}
                count={brigadistas.length}
                limit={10}
                members={brigadistas}
                onAdd={() => openNew('brigadista')}
                onDelete={setConfirmDelete}
                color="#0052FF"
                testid="brigadistas-section"
              />
              <TeamSection
                title="Gestores"
                icon={Briefcase}
                count={gestores.length}
                limit={2}
                members={gestores}
                onAdd={() => openNew('gestion')}
                onDelete={setConfirmDelete}
                color="#10B981"
                testid="gestores-section"
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="kpis" className="mt-4">
          {kpis.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <TrendingUp className="w-12 h-12 text-[#E4E4E7] mx-auto mb-4" />
                <p className="text-lg font-semibold text-[#0A0A0A]">Sin datos de KPIs</p>
                <p className="text-sm text-[#52525B]">Agrega brigadistas para ver su desempeño</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {kpis.map(k => (
                <Card data-testid={`kpi-card-${k.brigadista.id}`} key={k.brigadista.id} className="bg-white border border-[#E4E4E7] rounded-md hover:border-[#10B981] hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md bg-[#0052FF]/10 flex items-center justify-center">
                          <Users className="w-5 h-5 text-[#0052FF]" />
                        </div>
                        <div>
                          <p className="text-base font-bold text-[#0A0A0A]">{k.brigadista.name}</p>
                          <p className="text-xs text-[#52525B]">{k.brigadista.email}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs border-[#10B981] text-[#10B981]">
                        {k.tasa_resolucion}% resolución
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <KpiBox icon={FileText} label="Total" value={k.total} color="#0052FF" />
                      <KpiBox icon={CheckCircle2} label="Resueltas" value={k.resueltas} color="#10B981" />
                      <KpiBox icon={Calendar} label="Mes" value={k.mes_actual} color="#8B5CF6" />
                      <KpiBox icon={Sun} label="Hoy" value={k.hoy} color="#F59E0B" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="member-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar {ROLE_LABEL[form.role]}</DialogTitle>
            <DialogDescription>
              Crea credenciales para un nuevo miembro de tu equipo. Heredará tu distrito y secciones.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger data-testid="member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="brigadista">Brigadista (campo)</SelectItem>
                  <SelectItem value="gestion">Gestor (seguimiento)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre completo</Label>
              <Input data-testid="member-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Juan Pérez" />
            </div>
            <div>
              <Label>Email (Google Login)</Label>
              <Input data-testid="member-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="miembro@ejemplo.com" />
            </div>
            <div>
              <Label>Teléfono WhatsApp (OTP)</Label>
              <Input data-testid="member-phone" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+5215512345678" />
              <p className="text-[10px] text-[#52525B] mt-1">Uno de los dos: Email + Google o Teléfono + WhatsApp</p>
            </div>
            <div>
              <Label>Contraseña (opcional)</Label>
              <Input data-testid="member-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="member-submit" type="submit" disabled={saving} className="bg-[#0052FF] hover:bg-[#0048E5] text-white">
                {saving ? 'Guardando...' : 'Agregar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="confirm-delete-member" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Eliminar a {confirmDelete?.name}?</DialogTitle>
            <DialogDescription>El miembro perderá acceso. Las gestiones que creó se conservarán.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button data-testid="confirm-delete-member-btn" onClick={handleDelete} className="bg-[#EF4444] hover:bg-[#DC2626] text-white">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamSection({ title, icon: Icon, count, limit, members, onAdd, onDelete, color, testid }) {
  const canAdd = count < limit;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color }} />
          <h2 className="text-lg font-bold text-[#0A0A0A]">{title}</h2>
          <Badge variant="outline" className="text-xs">{count}/{limit}</Badge>
        </div>
        <Button
          data-testid={`add-${title.toLowerCase()}-btn`}
          onClick={onAdd}
          disabled={!canAdd}
          variant="outline"
          size="sm"
          className="rounded-md hover:bg-[#10B981] hover:text-white hover:border-[#10B981]"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Agregar
        </Button>
      </div>
      {members.length === 0 ? (
        <Card className="border-dashed border-[#E4E4E7]">
          <CardContent className="py-10 text-center text-sm text-[#52525B]">
            Sin {title.toLowerCase()} aún. Haz clic en "Agregar" para crear el primero.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {members.map(m => (
            <Card data-testid={`member-card-${m.id}`} key={m.id} className="bg-white border border-[#E4E4E7] rounded-md hover:border-[#10B981] hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-[#0A0A0A] truncate">{m.name}</p>
                    <p className="text-xs text-[#52525B] flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3" /> {m.email}
                    </p>
                  </div>
                </div>
                <Button
                  data-testid={`delete-member-${m.id}`}
                  onClick={() => onDelete(m)}
                  variant="outline"
                  size="sm"
                  className="w-full rounded-md text-[#EF4444] hover:bg-[#EF4444]/10 hover:text-[#EF4444] hover:border-[#EF4444]"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Eliminar
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function KpiBox({ icon: Icon, label, value, color }) {
  return (
    <div className="p-2 rounded-md bg-[#FAFAFA] text-center">
      <Icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color }} />
      <p className="text-xl font-black text-[#0A0A0A]">{value}</p>
      <p className="text-[9px] uppercase tracking-wider text-[#52525B] font-bold">{label}</p>
    </div>
  );
}
